/**
 * Loading State Hook
 *
 * Global loading state context for tracking async operations.
 * Provides unified loading indicator management across the application.
 *
 * Features:
 * - Track multiple concurrent operations by key
 * - Auto-timeout after 30s with warning
 * - Loading progress indicator component
 *
 * @example
 * // In component
 * const { startLoading, stopLoading, isLoading } = useLoadingState();
 *
 * const fetchData = async () => {
 *   startLoading('fetch-projects');
 *   try {
 *     await api.getProjects();
 *   } finally {
 *     stopLoading('fetch-projects');
 *   }
 * };
 *
 * @module hooks/useLoadingState
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';

/** @type {number} Auto-timeout duration in milliseconds */
const LOADING_TIMEOUT = 30000;

/** @type {React.Context} Loading context */
const LoadingContext = createContext(null);

/**
 * LoadingProvider Component
 *
 * Provides global loading state management to the app.
 * Wrap your app root with this provider.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const LoadingProvider = ({ children }) => {
  // Map of active loading operations: key -> { startTime, timeoutId }
  const [loadingKeys, setLoadingKeys] = useState(new Set());
  const loadingMeta = useRef(new Map());

  /**
   * Start tracking a loading operation
   * @param {string} key - Unique identifier for the operation
   */
  const startLoading = useCallback((key) => {
    setLoadingKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });

    // Set up auto-timeout
    const timeoutId = setTimeout(() => {
      console.warn(
        `[LoadingState] Operation "${key}" exceeded ${LOADING_TIMEOUT / 1000}s timeout`
      );
      // Auto-clear the loading state
      setLoadingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      loadingMeta.current.delete(key);
    }, LOADING_TIMEOUT);

    loadingMeta.current.set(key, {
      startTime: Date.now(),
      timeoutId,
    });
  }, []);

  /**
   * Stop tracking a loading operation
   * @param {string} key - Unique identifier for the operation
   */
  const stopLoading = useCallback((key) => {
    const meta = loadingMeta.current.get(key);
    if (meta?.timeoutId) {
      clearTimeout(meta.timeoutId);
    }
    loadingMeta.current.delete(key);

    setLoadingKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  /**
   * Clear all loading states
   */
  const clearAllLoading = useCallback(() => {
    // Clear all timeouts
    loadingMeta.current.forEach((meta) => {
      if (meta.timeoutId) {
        clearTimeout(meta.timeoutId);
      }
    });
    loadingMeta.current.clear();
    setLoadingKeys(new Set());
  }, []);

  /**
   * Check if a specific operation is loading
   * @param {string} key - Operation key to check
   * @returns {boolean} True if operation is loading
   */
  const isKeyLoading = useCallback(
    (key) => loadingKeys.has(key),
    [loadingKeys]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      loadingMeta.current.forEach((meta) => {
        if (meta.timeoutId) {
          clearTimeout(meta.timeoutId);
        }
      });
    };
  }, []);

  const value = {
    /** Whether any loading operation is active */
    isLoading: loadingKeys.size > 0,
    /** Set of currently loading operation keys */
    loadingKeys: Array.from(loadingKeys),
    /** Number of active loading operations */
    loadingCount: loadingKeys.size,
    /** Start tracking a loading operation */
    startLoading,
    /** Stop tracking a loading operation */
    stopLoading,
    /** Clear all loading states */
    clearAllLoading,
    /** Check if a specific operation is loading */
    isKeyLoading,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};

LoadingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * useLoadingState Hook
 *
 * Access global loading state from any component.
 *
 * @returns {Object} Loading state and controls
 * @throws {Error} If used outside LoadingProvider
 *
 * @example
 * const { startLoading, stopLoading, isLoading } = useLoadingState();
 */
export const useLoadingState = () => {
  const context = useContext(LoadingContext);

  if (!context) {
    throw new Error('useLoadingState must be used within a LoadingProvider');
  }

  return context;
};

/**
 * withLoadingState HOC
 *
 * Wrap a component with loading state tracking.
 *
 * @param {string} loadingKey - Key for this component's loading state
 * @returns {Function} HOC function
 *
 * @example
 * const EnhancedComponent = withLoadingState('my-component')(MyComponent);
 */
export const withLoadingState = (loadingKey) => (WrappedComponent) => {
  const WithLoadingStateComponent = (props) => {
    const { startLoading, stopLoading, isKeyLoading } = useLoadingState();

    return (
      <WrappedComponent
        {...props}
        startLoading={() => startLoading(loadingKey)}
        stopLoading={() => stopLoading(loadingKey)}
        isLoading={isKeyLoading(loadingKey)}
      />
    );
  };

  WithLoadingStateComponent.displayName = `WithLoadingState(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithLoadingStateComponent;
};

/**
 * useTrackedLoading Hook
 *
 * Simplified hook for tracking a single loading operation.
 *
 * @param {string} key - Unique key for this loading operation
 * @returns {Object} Loading controls for this specific operation
 *
 * @example
 * const { isLoading, start, stop } = useTrackedLoading('fetch-users');
 */
export const useTrackedLoading = (key) => {
  const { startLoading, stopLoading, isKeyLoading } = useLoadingState();

  return {
    isLoading: isKeyLoading(key),
    start: () => startLoading(key),
    stop: () => stopLoading(key),
  };
};

export default useLoadingState;
